import {create} from 'zustand';
import {w3cwebsocket} from 'websocket';
import {APP_VARS, toastUtils, UserManager} from 'core';
import {
  api,
  i18n,
  hasValue,
  isSameDayEx,
  createWsMessageOut,
  getCurrentTimezone,
  getIsoDateWithOffset,
  INewThreadData,
  EWSMessageTypeIn,
  EWSMessageTypeOut,
  IChatMessageData,
  IDocumentUpdateData,
  IChatMessageUpdateData,
  IThreadHistoryUpdateData,
  IWSMessageInHistoryData,
  IWSMessageOutSyncData,
  IAssistantResponseStatus,
  IWSMessageOutUserMessageData,
  IWSMessageOutSessionStateData,
  LocalEventEmitter
} from '@kcast-fe/core';

/* STORE ACTIONS */
type TStoreActions = {
  subscribe: () => Promise<void>;
  selectThread: (id: string, date: string) => void;
  clearThread: () => void;
  reset: () => void;

  _addListeners: () => void;
  _clearMessages: () => void;
};

/* STORE WS ACTIONS */
type TStoreWSActions = {
  loadMoreMessages: (force?: boolean) => void;
  sendTextMessage: (content: string) => void;
  setPingInterval: () => void;
  setSessionStateInterval: () => void;
};

/* STORE FIELDS */
type TStoreState = {
  chatKey: string | null;
  isConnecting: boolean;
  messages: Array<IChatMessageData>;

  isReconnecting: boolean;

  isThinking: boolean;
  thinkingStatus: string | null;
  isNotFinalMsgExists: boolean;

  _wsClient: w3cwebsocket | null;
  _wsPingInterval?: NodeJS.Timeout;
  _wsSessionStateInterval?: NodeJS.Timeout;

  hasMore: boolean;
  totalCount: number;
  firstItemIndex: number;
  filter: {id: string; date: string} | null;
  _from_timestamp: string;

  actions: TStoreActions;
  wsActions: TStoreWSActions;
};

const INITIAL_STATE: Omit<TStoreState, 'actions' | 'wsActions'> = {
  chatKey: null,
  messages: [],
  hasMore: false,
  totalCount: 0,
  isConnecting: false,
  isReconnecting: false,
  isNotFinalMsgExists: false,
  isThinking: false,
  thinkingStatus: null,
  filter: null,
  firstItemIndex: 0,
  _wsClient: null,
  _wsPingInterval: undefined,
  _wsSessionStateInterval: undefined,
  _from_timestamp: new Date().toISOString()
};

const PAGE_LIMIT = 100;
const PING_INTERVAL_MS = 60 * 1000;
const SESSION_STATE_INTERVAL_MS = 5 * 1000;
const RECONNECTIONS_TIMEOUT_MS = 5 * 1000;

export const useMessagesStore = create<TStoreState>()((set, get) => ({
  ...INITIAL_STATE,

  actions: {
    subscribe: async () => {
      get()._wsClient?.close();
      const wsClient = new w3cwebsocket(APP_VARS.WS_URL);
      set({_wsClient: wsClient, isConnecting: true, thinkingStatus: null});
      get().actions._addListeners();
    },

    selectThread: (threadId: string, timestamp: string) => {
      const prev = get().filter;
      if (!prev || prev.id !== threadId || !isSameDayEx(prev.date, timestamp)) {
        set(() => ({filter: {id: threadId, date: timestamp}}));
        get().wsActions.loadMoreMessages(true);
      } else {
        get().actions.clearThread();
      }
    },

    clearThread: () => {
      if (get().filter !== null) {
        set(() => ({filter: null}));
        get().wsActions.loadMoreMessages(true);
      }
    },

    /* ADD WS LISTENERS */
    _addListeners: () => {
      const {_wsClient} = get();
      if (_wsClient) {
        /* WS CLIENT CONNECTED */
        _wsClient.onopen = async () => {
          console.info('[WS] Client connected.');
          const user = await UserManager.getUser();
          const token = user?.access_token ?? '';
          const msgOut = createWsMessageOut(EWSMessageTypeOut.Auth, {token});
          _wsClient?.send(msgOut);
        };

        /* WS CLIENT MESSAGE */
        _wsClient.onmessage = (e) => {
          if (typeof e.data === 'string') {
            const dataJson = JSON.parse(e.data);
            const dataType = dataJson as {type: EWSMessageTypeIn};

            switch (dataType.type) {
              /* AUTH SUCCESS EVENT */
              case EWSMessageTypeIn.AuthSuccess: {
                console.info('[WS] Client auth success.');

                /* A new connection */
                get().wsActions.loadMoreMessages(true);
                set({isConnecting: false, isReconnecting: false});
                get().wsActions.setPingInterval();
                get().wsActions.setSessionStateInterval();
                break;
              }

              /* PONG EVENT */
              case EWSMessageTypeIn.Pong: {
                break;
              }

              /* ERROR EVENT */
              case EWSMessageTypeIn.Error: {
                toastUtils.error({message: i18n.t('toasts.responseError')});
                break;
              }

              /* "LOAD MORE" EVENT */
              case EWSMessageTypeIn.History: {
                const dataJsonCasted = dataJson as {data: IWSMessageInHistoryData};
                const {total_amount, history} = dataJsonCasted.data;

                const messages: IChatMessageData[] = history[0].messages;
                const list: IChatMessageData[] = messages.map((item, idx) => {
                  const prevThreadId = idx === 0 ? item.thread_id : messages[idx - 1]?.thread_id;
                  const prevCreatedAt = idx === 0 ? undefined : messages[idx - 1]?.created_at;
                  return {...item, prevThreadId, prevCreatedAt};
                });

                // FYI: Add for testing attachments
                // list.push(...MESSAGES_MOCKS);

                set((prev) => ({
                  totalCount: total_amount,
                  messages: [...list, ...prev.messages],
                  hasMore: total_amount - list.length - prev.messages.length > 0,
                  firstItemIndex:
                    prev.totalCount === 0
                      ? total_amount - list.length
                      : prev.firstItemIndex - list.length
                }));

                if (list.length > 0) {
                  set(() => ({_from_timestamp: list[0].created_at}));
                }
                break;
              }

              /* A NEW MSG EVENT */
              case EWSMessageTypeIn.UserMessage:
              case EWSMessageTypeIn.AssistantResponse: {
                const dataJsonCasted = dataJson as {data: IChatMessageData};
                const message = dataJsonCasted.data;

                if (dataType.type === EWSMessageTypeIn.UserMessage) {
                  set({isThinking: true});
                }

                if (dataType.type === EWSMessageTypeIn.AssistantResponse) {
                  set({isThinking: false, isNotFinalMsgExists: !message.is_final});

                  if (message.is_final) {
                    set({thinkingStatus: null});
                  }
                }

                set((prev) => {
                  const index = prev.messages.findIndex((m) => m.id === message.id);
                  const prevMsg = prev.messages[prev.messages.length - 1];
                  const nextMsg = {
                    ...message,
                    prevThreadId: prevMsg?.thread_id,
                    prevCreatedAt: prevMsg?.created_at
                  };

                  if (index === -1) {
                    prev.messages.push(nextMsg);
                    return {messages: prev.messages};
                  } else {
                    LocalEventEmitter.emit('MsgTextChunk', message);
                    prev.messages[index] = nextMsg;
                    return {messages: prev.messages};
                  }
                });
                break;
              }

              /* TOOL IS STARTED */
              case EWSMessageTypeIn.AssistantResponseStatus: {
                const dataJsonCasted = dataJson as {data: IAssistantResponseStatus};
                const payload = dataJsonCasted.data;
                set({thinkingStatus: payload.label});
                break;
              }

              /* UPDATE MESSAGE EVENT */
              case EWSMessageTypeIn.MessageUpdates: {
                const dataJsonCasted = dataJson as {data: Array<IChatMessageUpdateData>};
                const payloads = dataJsonCasted.data;

                payloads.forEach((payload) => {
                  if (hasValue(payload.thread_id)) {
                    set((prev) => {
                      const foundIndex = prev.messages.findIndex((m) => m.id === payload.id);
                      if (foundIndex >= 0) {
                        // Change thread id for a message
                        const newThreadId = payload.thread_id ?? undefined;
                        prev.messages[foundIndex].thread_id = newThreadId;
                        LocalEventEmitter.emit(
                          'MsgThreadChanged',
                          payload.id,
                          newThreadId,
                          prev.messages[foundIndex].prevThreadId
                        );

                        // Change prev thread id for next message
                        const nextMessage = prev.messages[foundIndex + 1];
                        if (nextMessage) {
                          prev.messages[foundIndex + 1].prevThreadId = newThreadId;
                          LocalEventEmitter.emit(
                            'MsgThreadChanged',
                            nextMessage.id,
                            nextMessage.thread_id ?? '',
                            newThreadId
                          );
                        }
                      }
                      return {messages: prev.messages};
                    });
                  }
                });
                break;
              }

              /* UPDATE MESSAGE EVENT */
              case EWSMessageTypeIn.ThreadHistoryUpdate: {
                const dataJsonCasted = dataJson as {data: IThreadHistoryUpdateData};
                api.updater.threads.threadHistoryQueryOptimisticUpdate(dataJsonCasted.data);
                break;
              }

              /* NEW THREAD EVENT */
              case EWSMessageTypeIn.NewThread: {
                const dataJsonCasted = dataJson as {data: INewThreadData};
                api.updater.threads.threadsQueryOptimisticAdd(dataJsonCasted.data);
                break;
              }

              /* DOCUMENT STATUS CHANGED EVENT */
              case EWSMessageTypeIn.DocumentStatusUpdate: {
                const dataJsonCasted = dataJson as {data: IDocumentUpdateData};
                api.updater.documents.documentOptimisticUpdate(dataJsonCasted.data.document);
                break;
              }

              default: {
                toastUtils.error({message: i18n.t('toasts.unknownMessage', {type: dataType.type})});
              }
            }
          }
        };

        /* WS CLIENT ERROR */
        _wsClient.onerror = (e) => {
          console.error('[WS] Client error', e);
        };

        /* WS CLIENT CLOSED */
        _wsClient.onclose = (e) => {
          console.info(
            `[WS] Client closed. Code is "${e.code || 'empty'}". Reason is "${e.reason || 'empty'}".`
          );

          // Token is not valid or expired
          if (e.code === 4001) {
            UserManager.signoutRedirect().then();
            return;
          }

          clearInterval(get()._wsPingInterval);
          clearInterval(get()._wsSessionStateInterval);
          set({isReconnecting: true, thinkingStatus: null});

          /* Try to reconnect */
          setTimeout(() => {
            console.info(`[WS] Reconnect attempt...`);
            get().actions.subscribe().then();
          }, RECONNECTIONS_TIMEOUT_MS);
        };
      }
    },

    _clearMessages: () => {
      const _from_timestamp = new Date().toISOString();
      set({messages: [], totalCount: 0, hasMore: false, firstItemIndex: 0, _from_timestamp});
    },

    reset: () => {
      get()._wsClient?.close();
      set(INITIAL_STATE);
    }
  },

  wsActions: {
    /* ADD WS PING INTERVAL */
    setPingInterval: () => {
      const pingInterval = setInterval(() => {
        if (get()._wsClient?.readyState === WebSocket.OPEN) {
          const msgOut = createWsMessageOut(EWSMessageTypeOut.Ping, null);
          get()._wsClient?.send(msgOut);
        }
      }, PING_INTERVAL_MS);

      set({_wsPingInterval: pingInterval});
    },

    /* ADD WS SESSION STATE INTERVAL */
    setSessionStateInterval: () => {
      const sessionStateInterval = setInterval(() => {
        // Only report while this tab is visible and the browser window is the
        // focused foreground window (i.e. not a background tab, not another app
        // in front, and not minimized to the tray).
        const isActive = document.visibilityState === 'visible' && document.hasFocus();

        if (isActive && get()._wsClient?.readyState === WebSocket.OPEN) {
          const data: IWSMessageOutSessionStateData = {focused: true};
          const msgOut = createWsMessageOut(EWSMessageTypeOut.SessionState, data);
          get()._wsClient?.send(msgOut);
        }
      }, SESSION_STATE_INTERVAL_MS);

      set({_wsSessionStateInterval: sessionStateInterval});
    },

    /* FETCH NEXT PORTION OF MESSAGES */
    loadMoreMessages: async (force?: boolean) => {
      if (force) {
        get().actions._clearMessages();
        set(() => ({chatKey: new Date().toISOString(), _from_timestamp: new Date().toISOString()}));
      }

      if (force || get().hasMore) {
        const {filter} = get();
        const syncData: IWSMessageOutSyncData = filter
          ? {
              thread_id: filter.id ?? null,
              from_timestamp: get()._from_timestamp,
              to_timestamp: force ? filter.date : null,
              limit: force ? null : PAGE_LIMIT
            }
          : {
              thread_id: null,
              from_timestamp: get()._from_timestamp,
              to_timestamp: null,
              limit: PAGE_LIMIT
            };

        const msgOut = createWsMessageOut(EWSMessageTypeOut.Sync, syncData);
        get()._wsClient?.send(msgOut);
      }
    },

    sendTextMessage: (content: string) => {
      const messageData: IWSMessageOutUserMessageData = {
        type: 'text',
        text: content,
        is_final: true,
        timezone: getCurrentTimezone(),
        client_datetime: getIsoDateWithOffset()
      };

      const msgOut = createWsMessageOut(EWSMessageTypeOut.UserMessage, messageData);
      get()._wsClient?.send(msgOut);
    }
  }
}));
